
import React, { useEffect, useRef, useState } from 'react';
import Gantt from 'frappe-gantt';
import { Activity, Dependency } from '../types.ts';
import { api } from '../services/api.ts';
import { useToast } from '../hooks/useToast.ts';

// Add custom styles for the chart to match our aesthetic
const GANTT_CSS = `
    .gantt-container {
        font-family: 'Inter', sans-serif !important;
    }
    .gantt .bar-label {
        font-size: 10px;
        font-weight: 700;
        fill: #555;
    }
    .gantt .bar-progress {
        fill: #000;
    }
    .gantt .bar {
        fill: #e4e4e7; /* zinc-200 */
    }
    .gantt .bar.status-completed {
        fill: #34d399; /* emerald-400 */
    }
    .gantt .bar.status-in-progress {
        fill: #fbbf24; /* amber-400 */
    }
    .gantt .today-highlight {
        fill: #f4f4f5;
        opacity: 0.5;
    }
`;

interface GanttChartProps {
    activities: Activity[];
    dependencies: Dependency[];
    projectId: number;
}

export const GanttChart: React.FC<GanttChartProps> = ({ activities, dependencies, projectId }) => {
    const ganttRef = useRef<HTMLDivElement>(null);
    const [ganttInstance, setGanttInstance] = useState<any>(null);
    const { addToast } = useToast();

    // Transform data for Frappe Gantt
    const tasks = activities.map(act => {
        // Find dependencies for this task
        const preds = dependencies
            .filter(d => d.successorActivityId === act.id)
            .map(d => d.predecessorActivityId) // IDs of predecessors
            .join(',');

        let cssClass = 'status-not-started';
        if (act.status === 'Completed') cssClass = 'status-completed';
        if (act.status === 'In Progress') cssClass = 'status-in-progress';

        return {
            id: act.id,
            name: act.title,
            start: act.startDate.split('T')[0], // YYYY-MM-DD
            end: act.finishDate.split('T')[0],
            progress: act.percentComplete,
            dependencies: preds,
            custom_class: cssClass
        };
    });

    useEffect(() => {
        if (!ganttRef.current || tasks.length === 0) return;

        // Frappe Gantt renders into the element
        const newGantt = new Gantt(ganttRef.current, tasks, {
            header_height: 50,
            column_width: 30,
            step: 24,
            view_modes: ['Quarter Day', 'Half Day', 'Day', 'Week', 'Month'],
            bar_height: 20,
            bar_corner_radius: 3,
            arrow_curve: 5,
            padding: 18,
            view_mode: 'Day',
            date_format: 'YYYY-MM-DD',
            language: 'en', // or 'es', 'it', 'ru', 'ptBr', 'fr', 'tr', 'zh', 'de', 'hu'
            custom_popup_html: null,

            // Events
            on_date_change: async (task: any, start: Date, end: Date) => {
                // Convert back to ISO string for our API
                const newStart = start.toISOString();
                const newEnd = end.toISOString();

                // Calculate new duration? Not strictly needed as API handles it? 
                // Whatever logic we want.

                await api.activities.update(task.id, {
                    startDate: newStart,
                    finishDate: newEnd,
                    // We could update remainingDuration based on diff here?
                    // For now, let's trust the drag.
                });
                addToast({ type: 'success', title: 'Schedule Updated', message: `Moved "${task.name}"` });
            },

            on_progress_change: async (task: any, progress: number) => {
                await api.activities.update(task.id, {
                    percentComplete: progress,
                    status: progress === 100 ? 'Completed' : progress > 0 ? 'In Progress' : 'Not Started'
                });
            },

            on_view_change: (mode: string) => {
                console.log(mode);
            }
        });

        setGanttInstance(newGantt);

        // Cleanup? logic for chart?
    }, [activities, dependencies]); // Re-render if data changes

    return (
        <div className="bg-white rounded-[2.5rem] p-8 border border-zinc-200 overflow-hidden shadow-sm animate-in fade-in duration-700">
            <style>{GANTT_CSS}</style>
            <div className="flex items-center justify-between mb-8">
                <h3 className="text-xl font-black uppercase tracking-tight">Timeline Visualization</h3>
                <div className="flex gap-2">
                    {['Day', 'Week', 'Month'].map(mode => (
                        <button
                            key={mode}
                            onClick={() => ganttInstance?.change_view_mode(mode)}
                            className="px-4 py-2 bg-zinc-50 border border-zinc-200 rounded-lg text-xs font-bold uppercase hover:bg-black hover:text-white transition-colors"
                        >
                            {mode}
                        </button>
                    ))}
                </div>
            </div>

            <div className="overflow-x-auto custom-scrollbar">
                <div ref={ganttRef} id="gantt-chart"></div>
            </div>

            <div className="mt-4 flex items-center gap-4 text-[10px] font-bold uppercase tracking-widest text-zinc-400">
                <div className="flex items-center gap-2"><div className="w-3 h-3 bg-[#e4e4e7] rounded-sm"></div> Not Started</div>
                <div className="flex items-center gap-2"><div className="w-3 h-3 bg-[#fbbf24] rounded-sm"></div> In Progress</div>
                <div className="flex items-center gap-2"><div className="w-3 h-3 bg-[#34d399] rounded-sm"></div> Completed</div>
            </div>
        </div>
    );
};
